import { Alert, Button } from "antd";
import { useVersionPoll } from "@/hooks/use_version_poll";
import { useVersionUiStore } from "@/state/version_state";

/**
 * Proactive "a newer version is deployed" bar. Complements the *reactive*
 * stale-chunk recovery in utils/chunk_reload.ts: this tells idle users to
 * reload BEFORE they hit a chunk 404, and it never auto-reloads (that would
 * discard in-progress work such as an open offer editor).
 *
 * Copy is verbatim from components/app_error_fallback.tsx; the orange CTA comes
 * from the app-wide colorPrimary (#ffa200) via `type="primary"`.
 *
 * Rendered fixed at the top, offset by the sider width so it never covers the
 * logo/nav. Dismissible per remote commit — a newer deploy re-arms it.
 */
export default function NewVersionBanner({ collapsed }: { collapsed: boolean }) {
  const { updateAvailable, manifest } = useVersionPoll();
  const { dismissedCommit, dismissBanner } = useVersionUiStore();

  const remoteCommit = manifest?.commit ?? null;
  if (!updateAvailable || !remoteCommit) return null;
  if (dismissedCommit === remoteCommit) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: collapsed ? 50 : 200,
        right: 0,
        zIndex: 1000,
      }}
    >
      <Alert
        banner
        type="info"
        showIcon
        message="Eine neue Version der Anwendung ist verfügbar. Bitte laden Sie die Seite neu."
        action={
          <Button size="small" type="primary" onClick={() => window.location.reload()}>
            Neu laden
          </Button>
        }
        closable
        onClose={() => dismissBanner(remoteCommit)}
      />
    </div>
  );
}
